import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TuiLanguageSwitcherService } from '@taiga-ui/i18n';
import { EMPTY, tap } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { mapLanguageToTui, saveLanguageToStorage } from '@common/utils/lang';
import { UsersService } from '@services/users.service';
import { WebSocketService } from '@services/websocket.service';
import { getCurrentUser, storeCurrentUser } from '@store/actions/current_user.actions';
import { initGeozonesVisibleOnMap } from '@store/geozones/geozones.actions';

import { MonitoringService } from '../../app/portal/pages/monitoring-page/monitoring.service';

@Injectable()
export class CurrentUserEffects {
  private readonly switcher = inject<TuiLanguageSwitcherService>(TuiLanguageSwitcherService);
  private readonly translate = inject(TranslateService);
  private readonly actions$ = inject(Actions);
  private readonly usersService = inject(UsersService);
  private readonly websocketService = inject(WebSocketService);
  private readonly monitoringService = inject(MonitoringService);
  private readonly router = inject(Router);
  private readonly store = inject(Store);

  getCurrentUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getCurrentUser),
      exhaustMap(() =>
        this.usersService.getCurrentUser().pipe(
          tap((user: any) => {
            this.translate.use(user.language);
            this.switcher.setLanguage(mapLanguageToTui(user.language));
            saveLanguageToStorage(user.language);
            sessionStorage.setItem(
              'timezone',
              `UTC${user.timezone >= 0 ? '+' : ''}${user.timezone}`,
            );

            const { configListItem, configContextMenu } = user.monitoringItemSetup;
            this.monitoringService.configListItem = configListItem;
            this.monitoringService.configContextMenu = configContextMenu;

            const geozoneIdsSet = new Set(user.visibleGeozoneIds);
            const obj = Object.fromEntries(Array.from(geozoneIdsSet, (id) => [id, true]));
            this.store.dispatch(initGeozonesVisibleOnMap({ visibleOnMap: obj }));
          }),
          map((currentUser: any) => {
            this.websocketService.connectUserSocket(currentUser);
            return storeCurrentUser({ currentUser });
          }),
          catchError(() => EMPTY),
        ),
      ),
    ),
  );

  getSegmentCurrentRoute(): string {
    const currentUrl = this.router.routerState.snapshot.url;
    return currentUrl.substring(currentUrl.lastIndexOf('/') + 1);
  }
}
